import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { srcset } from '@/helper/srcset';

const ImageGallery = (props: any) => {
  const { locale, asPath }: any = useRouter();
  const { facilityData } = props;

  const initialImage =
    facilityData?.attributes?.Facility_Gallery?.[0]?.Image?.data?.attributes;

  const [selectedImage, setSelectedImage] = useState({
    url: initialImage?.url || '',
    alternativeText: initialImage?.alternativeText || '',
  });
  const [toggleVideo, setToggleVideo] = useState(false);
  const [switchUrl, setSwitchUrl] = useState(false);
  const [prevRoute, setPrevRoute] = useState('');
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);
  const [autoPlay, setAutoPlay] = useState(0);
  const [hoverState, setHoverState] = useState({
    videoHover: false,
    popupVideoHover: false,
  });
  const [videoEnded, setVideoEnded] = useState({
    mainVideo: false,
    popupVideo: false,
  });

  const handleVideoHover = (
    type: 'videoHover' | 'popupVideoHover',
    state: boolean
  ) => {
    setHoverState((prev) => ({ ...prev, [type]: state }));
  };

  const handleVideoEnd = (type: 'mainVideo' | 'popupVideo') => {
    setVideoEnded((prev) => ({ ...prev, [type]: true }));
  };

  const handleModalClose = () => setAutoPlay(0);

  useEffect(() => {
    const switchLocale = JSON.parse(sessionStorage.getItem('switchLocale'));
    const currentUrl = {
      path: asPath,
      locale,
      domain: process.env.NEXT_PUBLIC_SITE_URL_ORIGIN,
      switchLocale,
    };
    const preRoute = JSON.parse(sessionStorage.getItem('preRoute'));
    const getPreRoute = JSON.parse(sessionStorage.getItem('prevRoute'));
    const getUpdateRoute = JSON.parse(sessionStorage.getItem('updateRou'));
    if (
      getPreRoute &&
      getPreRoute.locale === locale &&
      (preRoute !== getUpdateRoute || (!preRoute && !getUpdateRoute))
    ) {
      setPrevRoute(getPreRoute.path);
      setSwitchUrl(false);
      sessionStorage.setItem('preRoute', JSON.stringify(currentUrl));
      sessionStorage.setItem('switchLocale', JSON.stringify(locale));
    } else {
      setSwitchUrl(true);
      if (preRoute && locale !== switchLocale) {
        const { switchLocale: switchRou, domain, path, locale: lng } = preRoute;
        const url =
          preRoute && switchRou
            ? `${domain}/${switchRou}${path}`
            : `${domain}/${lng}${path}`;
        setPrevRoute(url);
        sessionStorage.setItem('updateRou', JSON.stringify(currentUrl));
        sessionStorage.setItem('prevRoute', null);
      } else if (getUpdateRoute && preRoute && preRoute !== getUpdateRoute) {
        const { domain, path, locale: lng } = getUpdateRoute;
        const url =
          getUpdateRoute && getUpdateRoute.locale && `${domain}/${lng}${path}`;
        setPrevRoute(url);
        sessionStorage.setItem('preRoute', JSON.stringify(currentUrl));
        sessionStorage.setItem('prevRoute', null);
      } else if (!preRoute && !getUpdateRoute && !getPreRoute) {
        setPrevRoute(
          `${process.env.NEXT_PUBLIC_SITE_URL_ORIGIN}/${locale}${asPath}`
        );
        sessionStorage.setItem('preRoute', JSON.stringify(currentUrl));
        sessionStorage.setItem('switchLocale', JSON.stringify(locale));
      } else {
        setPrevRoute(
          `${process.env.NEXT_PUBLIC_SITE_URL_ORIGIN}/${locale}${asPath}`
        );
      }
    }
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const closeButton = document.querySelector(
          '[data-bs-dismiss="modal"]'
        ) as HTMLElement;
        closeButton?.click();
        handleModalClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, []);

  const renderImageOrVideo = (image: any, index: number) => {
    if (image?.Short_Video?.data?.attributes?.url) {
      return (
        <div
          className="h-100 position-relative overflow-hidden sv-play-video-wrapper"
          onMouseEnter={() => handleVideoHover('videoHover', true)}
          onMouseLeave={() => handleVideoHover('videoHover', false)}
        >
          <video
            width="100%"
            height="100%"
            style={{ objectFit: 'cover' }}
            controls={false}
            autoPlay
            muted
            playsInline
            onEnded={() => handleVideoEnd('mainVideo')}
          >
            <source
              src={image?.Short_Video?.data?.attributes?.url}
              type="video/mp4"
            />
          </video>
          <div
            className={`sv-images-overlayLayer ${
              videoEnded.mainVideo || hoverState.videoHover
                ? 'overlay-visible'
                : ''
            }`}
          ></div>
          <div
            className="sv-image-gallery-btns"
            style={{
              display:
                videoEnded.mainVideo || hoverState.videoHover ? 'flex' : 'none',
            }}
          >
            <button
              className="sv-video-btn d-flex align-items-center"
              onClick={() => {
                setToggleVideo(true);
                setIsLoadingIframe(true);
                setAutoPlay(1);
              }}
            >
              <span className="sv-btn-icon me-1">
                <img
                  src="/images/sv-play-video-white-icon.svg"
                  alt={locale === 'en' ? 'Play Video' : 'Voir vidéo'}
                />
              </span>
              <FormattedMessage id="playVideo" />
            </button>
          </div>
        </div>
      );
    }

    if (index === 4) {
      return (
        <div className="h-100 position-relative overflow-hidden sv-play-video-wrapper">
          <img
            {...srcset(
              image?.Image?.data?.attributes?.formats?.small?.url ??
                image?.Image?.data?.attributes?.url,
              120,
              null,
              null
            )}
            alt={
              image?.Image?.data?.attributes?.alternativeText ||
              'Facility image'
            }
            loading="lazy"
          />
          <div className="sv-images-overlayLayer overlay-visible"></div>
          <div className="sv-image-gallery-btns">
            <button
              className="sv-img-btn d-flex align-items-center"
              aria-label={locale === 'en' ? '10+ Photos' : 'Photos 10+'}
              data-bs-toggle="modal"
              data-bs-target="#extraImagesModal"
            >
              <span className="sv-btn-icon">
                <img src="/images/sv-images-icon.svg" alt="Images" />
              </span>
              {locale === 'en' ? '10+ Photos' : 'Photos 10+'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <img
        {...srcset(
          image?.Image?.data?.attributes?.formats?.[
            index === 0 ? 'medium' : 'small'
          ]?.url ?? image?.Image?.data?.attributes?.url,
          120,
          null,
          null
        )}
        alt={
          image?.Image?.data?.attributes?.alternativeText || 'Facility image'
        }
        loading="lazy"
      />
    );
  };

  return (
    <div className="sv-storage-image-gallery">
      {facilityData?.attributes?.Facility_Gallery && (
        <div className="images overflow-hidden">
          {facilityData?.attributes?.Facility_Gallery?.slice(1, 6).map(
            (image: any, index: number) => (
              <div
                key={image?.Image?.data?.attributes?.url || index}
                className="img-item position-relative"
                data-bs-toggle="modal"
                data-bs-target="#extraImagesModal"
                onClick={() => {
                  if (index === 3) {
                    setToggleVideo(true);
                    setIsLoadingIframe(true);
                    setAutoPlay(1);
                  } else {
                    setSelectedImage(image?.Image?.data?.attributes);
                    setToggleVideo(false);
                    setAutoPlay(0);
                  }
                }}
              >
                {renderImageOrVideo(image, index)}

                {index === 3 && (
                  <>
                    {!image?.Short_Video?.data?.attributes?.url && (
                      <div className="sv-overlay-container">
                        <div className="sv-images-overlayLayer" />
                        <div className="sv-image-gallery-btns sv-image-video-play">
                          <button
                            className="btn sv-play-video-btn position-absolute center-button z-index-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setToggleVideo(true);
                              setIsLoadingIframe(true);
                              setAutoPlay(1);
                            }}
                          >
                            <span className="sv-btn-icon me-1">
                              <img
                                src="/images/sv-play-video-white-icon.svg"
                                alt={
                                  locale === 'en' ? 'Play Video' : 'Voir vidéo'
                                }
                              />
                            </span>
                            <FormattedMessage id="playVideo" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          )}
        </div>
      )}

      <div className="sv-image-gallery-btns top-0">
        {!switchUrl ? (
          <Link href={prevRoute} passHref>
            <button className="btn sv-back-btn">
              <span className="sv-btn-icon">
                <img src="/images/sv-back-btn-arrow.svg" alt="Back Arrow" />
              </span>
              <FormattedMessage id="back" />
            </button>
          </Link>
        ) : (
          <button
            className="btn sv-back-btn"
            onClick={() => {
              window.location.href = prevRoute;
            }}
          >
            <span className="sv-btn-icon">
              <img src="/images/sv-back-btn-arrow.svg" alt="Back Arrow" />
            </span>
            <FormattedMessage id="back" />
          </button>
        )}
      </div>

      <div
        className="modal fade"
        id="extraImagesModal"
        role="dialog"
        aria-labelledby="extraImagesModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered container">
          <div className="modal-content">
            <div className="modal-header">
              <button
                type="button"
                className="btn-close sv-close-btn"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleModalClose}
              >
                <img src="/images/sv-modal-cross-icon.svg" alt="Cross icon" />
              </button>
            </div>
            <div className="modal-body">
              <div className="row p-0 m-0">
                <div className="col-md-9 px-2" id="big-image">
                  <div className="position-relative h-100 ratio ratio-3x2">
                    {toggleVideo ? (
                      <>
                        {isLoadingIframe && (
                          <div
                            className="d-flex justify-content-center align-items-center position-absolute top-50 start-50 translate-middle"
                            style={{ width: '100%', height: '100%', zIndex: 2 }}
                          >
                            <div
                              className="spinner-border text-light"
                              style={{ width: '4rem', height: '4rem' }}
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          </div>
                        )}
                        <iframe
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: '8px',
                            transform: 'translateZ(0px)',
                            zIndex: 1,
                          }}
                          src={`${facilityData?.attributes?.Facility_Gallery?.[0]?.Video_URL}?autoplay=${autoPlay}&muted=1&controls=1&loop=0`}
                          onLoad={() => setIsLoadingIframe(false)}
                          loading="lazy"
                          allow="autoplay; fullscreen"
                        />
                      </>
                    ) : (
                      <img
                        className="w-100 h-100"
                        style={{ objectFit: 'cover', borderRadius: '8px' }}
                        {...srcset(selectedImage?.url, 320, null, null)}
                        alt={selectedImage?.alternativeText}
                        loading="lazy"
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-3 p-0">
                  {facilityData?.attributes?.Facility_Gallery?.[4]?.Short_Video
                    ?.data?.attributes?.url ? (
                    <div
                      className={`position-relative ${
                        toggleVideo ? 'd-none' : ''
                      }`}
                      style={{ height: '173px', marginBottom: '7px' }}
                      onMouseEnter={() =>
                        handleVideoHover('popupVideoHover', true)
                      }
                      onMouseLeave={() =>
                        handleVideoHover('popupVideoHover', false)
                      }
                    >
                      <video
                        width="100%"
                        className="sv-modal-play-video"
                        height="100%"
                        style={{ objectFit: 'cover', borderRadius: '12px' }}
                        controls={false}
                        autoPlay
                        muted
                        playsInline
                        onEnded={() => handleVideoEnd('popupVideo')}
                      >
                        <source
                          src={
                            facilityData?.attributes?.Facility_Gallery?.[4]
                              ?.Short_Video?.data?.attributes?.url
                          }
                          type="video/mp4"
                        />
                      </video>
                      <div
                        style={{
                          display:
                            videoEnded.popupVideo || hoverState.popupVideoHover
                              ? 'flex'
                              : 'none',
                        }}
                      >
                        <div className="sv-images-overlayLayer sv-modal-play-video"></div>
                        <button
                          className="btn sv-play-video-btn position-absolute center-button z-index-1"
                          onClick={() => {
                            setToggleVideo(true);
                            setIsLoadingIframe(true);
                            setAutoPlay(1);
                          }}
                        >
                          <span className="sv-btn-icon me-1">
                            <img
                              src="/images/sv-play-video-white-icon.svg"
                              alt={
                                locale === 'en' ? 'Play Video' : 'Voir vidéo'
                              }
                            />
                          </span>
                          <FormattedMessage id="playVideo" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`position-relative ${
                        toggleVideo ? 'd-none' : ''
                      }`}
                      style={{ height: '173px', marginBottom: '7px' }}
                    >
                      <img
                        className="sv-modal-play-video"
                        style={{
                          height: '173px',
                          width: '100%',
                          marginBottom: '7px',
                          objectFit: 'cover',
                          borderRadius: '12px',
                        }}
                        {...srcset(
                          facilityData?.attributes?.Facility_Gallery?.[4]?.Image
                            ?.data?.attributes?.url,
                          320,
                          null,
                          null
                        )}
                        alt={
                          facilityData?.attributes?.Facility_Gallery?.[4]?.Image
                            ?.data?.attributes?.alternativeText
                        }
                        loading="lazy"
                      />

                      <div className="sv-overlay-container">
                        <div className="sv-images-overlayLayer sv-modal-play-video" />
                        <button
                          className="btn sv-play-video-btn position-absolute center-button z-index-2"
                          onClick={() => {
                            setToggleVideo(true);
                            setIsLoadingIframe(true);
                            setAutoPlay(1);
                          }}
                        >
                          <span className="sv-btn-icon me-1">
                            <img
                              src="/images/sv-play-video-white-icon.svg"
                              alt={
                                locale === 'en' ? 'Play Video' : 'Voir vidéo'
                              }
                            />
                          </span>
                          <FormattedMessage id="playVideo" />
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="img-scroll-container position-relative">
                    <div
                      className="img-scroll"
                      style={{ height: toggleVideo ? '590px' : '410px' }}
                    >
                      <div className="me-2">
                        {facilityData?.attributes?.Facility_Gallery?.slice(
                          1,
                          facilityData?.attributes?.Facility_Gallery?.length
                        ).map((image: any, index: number) => (
                          <img
                            className="small-images"
                            key={image?.Image?.data?.attributes?.url || index}
                            {...srcset(
                              image?.Image?.data?.attributes?.url,
                              200,
                              null,
                              null
                            )}
                            onClick={() => {
                              setSelectedImage(image?.Image?.data?.attributes);
                              setToggleVideo(false);
                            }}
                            alt={
                              image?.Image?.data?.attributes?.alternativeText
                            }
                            loading="lazy"
                          />
                        ))}
                      </div>
                    </div>
                    <div className="overlay-top"></div>
                    <div className="overlay-bottom"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageGallery;
